/**
 * The 25% grammar cap (pedagogical invariant 9, SPEC §6.3, DECISIONS D-018).
 *
 * > Conversión (tier 4) and Motor (tier 3) items **together** must never exceed **25% of items in
 * > any rolling seven-day window**. Enforce this in the round builder, not as advice.
 *
 * This module holds the accounting; `round/builder.ts` (T27c) holds the enforcement. Two numbers
 * come out of it and they are deliberately different:
 *
 *  - **reported share** — every attempt in the window, due reviews included. What the tracker
 *    shows. It can legitimately sit above 0.25 when review load happens to be grammar-heavy, and
 *    when it does it carries the reason `review-driven`. That is not a breach.
 *  - **discretionary share** — non-review draws only. What the cap actually governs, because
 *    invariant 7 outranks invariant 9: the cap limits what is *introduced and practised by
 *    choice*, never what is *due*.
 *
 * Why the cap exists at all: explicit grammar study is this learner's known over-investment
 * failure mode (BRIEF <conversion_engine>). Reviewing an already-learned grammar item is spaced
 * retrieval doing its job, not over-investment — so it is exempt.
 *
 * Pure. `now` comes from the caller's injected clock.
 */

import type { Tier } from '../state/gamestate';
import type { Attempt } from '../store/schema';

/** Motor (3) and Conversión (4). The two explicit-grammar tiers. */
export const GRAMMAR_TIERS: ReadonlySet<Tier> = new Set<Tier>([3, 4]);

export const GRAMMAR_CAP = 0.25;

/** Seven days, by the injected clock. Rolling, never bucketed by calendar day (SPEC §8.1). */
export const QUOTA_WINDOW_MS = 7 * 86_400_000;

export function isGrammarTier(tier: Tier): boolean {
  return GRAMMAR_TIERS.has(tier);
}

export interface QuotaConfig {
  cap: number;
  windowMs: number;
}

export const DEFAULT_QUOTA: QuotaConfig = Object.freeze({
  cap: GRAMMAR_CAP,
  windowMs: QUOTA_WINDOW_MS,
});

/**
 * The minimum the ledger needs to know about a draw. Deliberately not the whole `Attempt`: the
 * builder consults the ledger *before* an attempt exists, when all it has is a candidate.
 */
export interface DrawRecord {
  tier: Tier;
  at: number;
  /** A due review. Exempt from displacement, still counted in the reported share. */
  wasDueReview: boolean;
}

export function toDrawRecord(attempt: Attempt): DrawRecord {
  return { tier: attempt.tier, at: attempt.at, wasDueReview: attempt.wasDueReview };
}

export type ShareReason =
  /** Nothing in the window yet. */
  | 'no-data'
  /** Reported share is at or below the cap. */
  | 'under-cap'
  /**
   * Reported share is above the cap and the discretionary share is not. Expected, and not a
   * defect: due grammar reviews are exempt (SPEC §6.3).
   */
  | 'review-driven'
  /**
   * Discretionary share is above the cap.
   *
   * This does NOT by itself mean the builder admitted too much. A trailing window's share can rise
   * with nothing admitted at all: as older non-grammar draws age out of the seven days, the same
   * grammar draws become a larger fraction of a smaller denominator. Observed in the T28
   * simulation at round 25 — 12 grammar out of 44 discretionary draws, where each of the twelve
   * was admitted at or under the cap and four non-grammar draws had since expired.
   *
   * `admissionBreaches()` is what separates the two: an over-cap window with no admission breach
   * is arithmetic, and an over-cap window with one is a defect. See DECISIONS D-061.
   */
  | 'over-cap';

export interface GrammarShare {
  windowStart: number;
  windowEnd: number;
  /** All draws in the window. */
  total: number;
  grammar: number;
  /** Null when there is nothing in the window. Never 0 — those are different claims (D-012). */
  share: number | null;

  /** Non-review draws only: what the cap governs. */
  discretionaryTotal: number;
  discretionaryGrammar: number;
  discretionaryShare: number | null;

  reason: ShareReason;
}

function shareOf(grammar: number, total: number): number | null {
  return total === 0 ? null : grammar / total;
}

/** The rolling-window share, computed from scratch. Used by the tracker and by tests. */
export function grammarShare(
  draws: readonly DrawRecord[],
  now: number,
  cfg: QuotaConfig = DEFAULT_QUOTA,
): GrammarShare {
  const windowStart = now - cfg.windowMs;
  let total = 0;
  let grammar = 0;
  let discretionaryTotal = 0;
  let discretionaryGrammar = 0;

  for (const draw of draws) {
    if (draw.at <= windowStart || draw.at > now) continue;
    const isGrammar = isGrammarTier(draw.tier);
    total += 1;
    if (isGrammar) grammar += 1;
    if (!draw.wasDueReview) {
      discretionaryTotal += 1;
      if (isGrammar) discretionaryGrammar += 1;
    }
  }

  const share = shareOf(grammar, total);
  const discretionaryShare = shareOf(discretionaryGrammar, discretionaryTotal);

  let reason: ShareReason;
  if (total === 0) reason = 'no-data';
  else if (discretionaryShare !== null && discretionaryShare > cfg.cap) reason = 'over-cap';
  else if (share !== null && share > cfg.cap) reason = 'review-driven';
  else reason = 'under-cap';

  return {
    windowStart,
    windowEnd: now,
    total,
    grammar,
    share,
    discretionaryTotal,
    discretionaryGrammar,
    discretionaryShare,
    reason,
  };
}

/**
 * A running ledger for the round builder, so the cap can be checked **per draw** without
 * rescanning seven days of history on every single one.
 *
 * Constructed from the persisted window, then fed each draw as it is emitted. `wouldExceedCap`
 * asks the question SPEC §6.3 specifies: the share *as it would be after emitting this draw*.
 */
export class GrammarLedger {
  private readonly draws: DrawRecord[];
  private readonly horizonMs: number;

  /**
   * @param horizonMs How much further the clock will advance before the last draw this ledger
   *   governs is answered — in practice, the run length. Defaults to 0.
   *
   *   It exists because a rolling window's share can rise **without anything being admitted**: as
   *   older non-grammar draws age out of the trailing seven days, the same set of grammar draws
   *   becomes a larger fraction of a smaller denominator. Checking only the window as it stands at
   *   the start of a run therefore admits draws that breach the cap when re-measured forty seconds
   *   later. Found by the T28 simulation at round 25 (3/12 at admission, 3/11 one draw later).
   *   See DECISIONS D-061.
   */
  constructor(
    history: readonly DrawRecord[],
    private now: number,
    private readonly cfg: QuotaConfig = DEFAULT_QUOTA,
    horizonMs = 0,
  ) {
    this.horizonMs = Math.max(0, horizonMs);
    // Keep everything that any window reachable inside the horizon could contain.
    const earliestStart = now - cfg.windowMs;
    this.draws = history.filter((d) => d.at > earliestStart && d.at <= now);
  }

  /**
   * Would emitting a discretionary draw at this tier push the discretionary share over the cap?
   *
   * Checked against **every trailing window a measurement during this run could use**, not just
   * the one that exists right now. The candidate window starts are bounded and few: they span
   * `horizonMs`, and only the handful of draws recorded in that same slice seven days ago fall
   * inside it.
   *
   * Always false for a due review: a due review is never displaced, whatever it does to the
   * numbers (invariant 7 outranks invariant 9).
   */
  wouldExceedCap(tier: Tier, options: { wasDueReview: boolean } = { wasDueReview: false }): boolean {
    if (options.wasDueReview) return false;
    if (!isGrammarTier(tier)) return false;
    for (const start of this.candidateWindowStarts()) {
      const current = this.counts(start);
      const projected = (current.discretionaryGrammar + 1) / (current.discretionaryTotal + 1);
      if (projected > this.cfg.cap) return true;
    }
    return false;
  }

  /**
   * Window starts a measurement could use before the horizon runs out.
   *
   * The share as a function of window start only changes where an entry drops out, so evaluating
   * the two extremes plus every entry boundary between them covers every distinct window exactly.
   */
  private candidateWindowStarts(): number[] {
    const earliest = this.now - this.cfg.windowMs;
    const latest = this.now + this.horizonMs - this.cfg.windowMs;
    const starts = [earliest, latest];
    if (this.horizonMs > 0) {
      for (const draw of this.draws) {
        if (draw.at >= earliest && draw.at <= latest) starts.push(draw.at);
      }
    }
    return starts;
  }

  /** Record an emitted draw. Advances the window end if the draw is later than the current one. */
  record(draw: DrawRecord): void {
    this.draws.push(draw);
    if (draw.at > this.now) this.now = draw.at;
  }

  private counts(
    windowStart = this.now - this.cfg.windowMs,
  ): { discretionaryTotal: number; discretionaryGrammar: number } {
    let discretionaryTotal = 0;
    let discretionaryGrammar = 0;
    for (const draw of this.draws) {
      if (draw.at <= windowStart || draw.at > this.now || draw.wasDueReview) continue;
      discretionaryTotal += 1;
      if (isGrammarTier(draw.tier)) discretionaryGrammar += 1;
    }
    return { discretionaryTotal, discretionaryGrammar };
  }

  /** The full share report as of the ledger's current instant. */
  share(): GrammarShare {
    return grammarShare(this.draws, this.now, this.cfg);
  }

  /** Everything currently inside the window. Copy, so the caller cannot mutate the ledger. */
  window(): DrawRecord[] {
    const windowStart = this.now - this.cfg.windowMs;
    return this.draws.filter((d) => d.at > windowStart && d.at <= this.now).map((d) => ({ ...d }));
  }
}

/**
 * Discretionary grammar draws that were admitted while the share *including them* already exceeded
 * the cap.
 *
 * This is the auditable form of invariant 9, and the only form of it any admission policy can
 * actually guarantee. The literal retrospective reading — "the trailing seven-day share is never
 * above 0.25 at any instant" — is not achievable by any policy short of one that refuses grammar
 * almost entirely, because the share moves after admission as non-grammar draws expire out of the
 * window. What *is* achievable, and what the builder enforces, is that no draw was ever chosen
 * while it would breach the cap.
 *
 * An empty result is compliance. Anything in it is a builder defect.
 */
export function admissionBreaches(
  draws: readonly DrawRecord[],
  cfg: QuotaConfig = DEFAULT_QUOTA,
): DrawRecord[] {
  const ordered = [...draws].sort((a, b) => a.at - b.at);
  const discretionary: DrawRecord[] = [];
  const breaches: DrawRecord[] = [];

  let head = 0;
  let grammar = 0;

  for (const draw of ordered) {
    if (draw.wasDueReview) continue;
    discretionary.push(draw);
    if (isGrammarTier(draw.tier)) grammar += 1;

    // Slide the window forward to this draw's instant.
    const windowStart = draw.at - cfg.windowMs;
    while (head < discretionary.length && discretionary[head]!.at <= windowStart) {
      if (isGrammarTier(discretionary[head]!.tier)) grammar -= 1;
      head += 1;
    }

    const total = discretionary.length - head;
    if (isGrammarTier(draw.tier) && total > 0 && grammar / total > cfg.cap) breaches.push(draw);
  }

  return breaches;
}
